import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { defer, Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

/// What travels in the page for one answer: the answer itself, or the status of a request the server was refused.
interface Carried<T> {
    value?: T;
    status?: number;
}

/// An answer the server render asked for, carried to the browser in the page (TransferState): the browser draws what the
/// server drew without asking again, so nothing on the page changes as it wakes up. It is taken once - a later visit to
/// the same page asks afresh. The answer travels wrapped, so an empty list is carried like any other (ShopService.carried
/// asks again for one, which suits its shelves but not a list that is rightly empty). A 404 travels too: the browser goes
/// straight to the not-found page the server showed instead of asking to be told again. Any other failure is not
/// carried, and the browser asks for itself.
export function carried<T>(state: TransferState, platformId: any, key: string, ask: () => Observable<T>): Observable<T> {
    const stateKey = makeStateKey<Carried<T>>(key);

    return defer(() => {
        const kept = state.get<Carried<T>>(stateKey, null);
        if (kept && !isPlatformServer(platformId)) {
            state.remove(stateKey);
            return kept.status
                ? throwError(new HttpErrorResponse({ status: kept.status, statusText: 'Not Found' }))
                : of(kept.value);
        }

        return ask().pipe(
            tap(value => {
                if (isPlatformServer(platformId)) {
                    state.set<Carried<T>>(stateKey, { value });
                }
            }),
            catchError(error => {
                if (isPlatformServer(platformId) && error instanceof HttpErrorResponse && error.status === 404) {
                    state.set<Carried<T>>(stateKey, { status: 404 });
                }
                return throwError(error);
            }));
    });
}
